// saga.js

import { call, put, takeEvery } from "redux-saga/effects";
import * as actions from "./_actions";
import * as types from "./_actionTypes";
import StudentCourseHelper from "../../../../utils/API/BackendHelper/student/courses";

function* fetchAvailableCoursesSaga(action) {
  try {
    const response = yield call(StudentCourseHelper.getAvailableCourses);

    if (!response.success) {
      throw new Error(response.message || "Failed to fetch available courses");
    }

    yield put(
      actions.fetchAvailableCoursesSuccess({
        availableCourses: response.data || [],
      })
    );
  } catch (error) {
    console.error("Error fetching available courses:", error);
    yield put(actions.fetchAvailableCoursesFailure(error.message));
  }
}

// Fetch Enrolled Courses Saga
function* fetchEnrolledCoursesSaga(action) {
  try {
    const response = yield call(StudentCourseHelper.getEnrolledCourses);

    if (!response.success) {
      throw new Error(response.message || "Failed to fetch enrolled courses");
    }

    yield put(actions.fetchEnrolledCoursesSuccess(response.data || []));
  } catch (error) {
    console.error("Error fetching enrolled courses:", error);
    yield put(actions.fetchEnrolledCoursesFailure(error.message));
  }
}

// Enroll Course Saga
function* enrollCourseSaga(action) {
  try {
    const courseId = action.payload;
    const response = yield call(StudentCourseHelper.enrollInCourse, courseId);

    if (!response.success) {
      throw new Error(response.message || "Failed to enroll in course");
    }

    yield put(actions.fetchAvailableCoursesRequest());
    yield put(actions.fetchEnrolledCoursesRequest());
    yield put(actions.enrollCourseSuccess(response.data));
  } catch (error) {
    console.error("Error enrolling in course:", error);
    yield put(actions.enrollCourseFailure(error.message));
  }
}

// Unenroll Course Saga
function* unenrollCourseSaga(action) {
  try {
    const { courseOfferingId } = action.payload;
    console.log("Unenrolling from courseOfferingId:", action.payload);
    const response = yield call(StudentCourseHelper.unenrollFromCourse, {
      courseId: courseOfferingId,
    });

    if (!response.success) {
      throw new Error(response.message || "Failed to unenroll from course");
    }

    yield put(actions.unenrollCourseSuccess(response.data));
    yield put(actions.fetchEnrolledCoursesRequest());
  } catch (error) {
    console.error("Error unenrolling from course:", error);
    yield put(actions.unenrollCourseFailure(error.message));
  }
}

// Student Fetch Course Contents Saga
function* fetchStudentCourseContentsSaga(action) {
  try {
    const { courseId, filters } = action.payload;
    console.log("Fetching student course contents");
    const response = yield call(
      StudentCourseHelper.getCourseContents,
      courseId,
      filters
    );

    if (!response.success) {
      throw new Error(response.message || "Failed to fetch course contents");
    }

    yield put(actions.fetchStudentCourseContentsSuccess(response.data || []));
  } catch (error) {
    console.error("Error fetching student course contents:", error);
    yield put(actions.fetchStudentCourseContentsFailure(error.message));
  }
}

// Student Fetch Content Stats Saga
function* fetchStudentContentStatsSaga(action) {
  try {
    const { courseId } = action.payload;
    const response = yield call(StudentCourseHelper.getContentStats, courseId);

    if (!response.success) {
      throw new Error(response.message || "Failed to fetch content stats");
    }

    yield put(actions.fetchStudentContentStatsSuccess(response.data));
  } catch (error) {
    console.error("Error fetching student content stats:", error);
    yield put(actions.fetchStudentContentStatsFailure(error.message));
  }
}

// Student Fetch Content By ID Saga
function* fetchStudentContentByIdSaga(action) {
  try {
    const { contentId } = action.payload;
    const response = yield call(StudentCourseHelper.getContentById, contentId);

    if (!response.success) {
      throw new Error(response.message || "Failed to fetch content");
    }

    yield put(actions.fetchStudentContentByIdSuccess(response.data));
  } catch (error) {
    console.error("Error fetching student content by ID:", error);
    yield put(actions.fetchStudentContentByIdFailure(error.message));
  }
}

// Student Update Content Progress Saga
function* updateStudentContentProgressSaga(action) {
  try {
    const { contentId, progressData } = action.payload;
    const response = yield call(
      StudentCourseHelper.updateContentProgress,
      contentId,
      progressData
    );

    if (!response.success) {
      throw new Error(response.message || "Failed to update progress");
    }

    yield put(actions.updateStudentContentProgressSuccess(contentId, response.data));
  } catch (error) {
    console.error("Error updating student content progress:", error);
    yield put(actions.updateStudentContentProgressFailure(error.message));
  }
}

// Student Mark Content Complete Saga
function* markStudentContentCompleteSaga(action) {
  try {
    const { contentId } = action.payload;
    const response = yield call(
      StudentCourseHelper.markContentComplete,
      contentId
    );

    if (!response.success) {
      throw new Error(response.message || "Failed to mark content as complete");
    }

    yield put(actions.markStudentContentCompleteSuccess(contentId));
  } catch (error) {
    console.error("Error marking student content complete:", error);
    yield put(actions.markStudentContentCompleteFailure(error.message));
  }
}

// Student Fetch Next Content Saga
function* fetchStudentNextContentSaga(action) {
  try {
    const { courseId } = action.payload;
    const response = yield call(StudentCourseHelper.getNextContent, courseId);

    if (!response.success) {
      throw new Error(response.message || "Failed to fetch next content");
    }

    yield put(actions.fetchStudentNextContentSuccess(response.data));
  } catch (error) {
    console.error("Error fetching student next content:", error);
    yield put(actions.fetchStudentNextContentFailure(error.message));
  }
}

function* studentCoursesSaga() {
  yield takeEvery(
    types.FETCH_AVAILABLE_COURSES_REQUEST,
    fetchAvailableCoursesSaga
  );
  yield takeEvery(
    types.FETCH_ENROLLED_COURSES_REQUEST,
    fetchEnrolledCoursesSaga
  );
  yield takeEvery(types.ENROLL_COURSE_REQUEST, enrollCourseSaga);
  yield takeEvery(types.UNENROLL_COURSE_REQUEST, unenrollCourseSaga);
  yield takeEvery(
    types.FETCH_STUDENT_COURSE_CONTENTS_REQUEST,
    fetchStudentCourseContentsSaga
  );
  yield takeEvery(
    types.FETCH_STUDENT_CONTENT_STATS_REQUEST,
    fetchStudentContentStatsSaga
  );
  yield takeEvery(
    types.FETCH_STUDENT_CONTENT_BY_ID_REQUEST,
    fetchStudentContentByIdSaga
  );
  yield takeEvery(
    types.UPDATE_STUDENT_CONTENT_PROGRESS_REQUEST,
    updateStudentContentProgressSaga
  );
  yield takeEvery(
    types.MARK_STUDENT_CONTENT_COMPLETE_REQUEST,
    markStudentContentCompleteSaga
  );
  yield takeEvery(
    types.FETCH_STUDENT_NEXT_CONTENT_REQUEST,
    fetchStudentNextContentSaga
  );
}

export default studentCoursesSaga;